import { motion } from "framer-motion";
import { StageType } from "./IndustryLifeCycle";
import { useTranslation } from "react-i18next";

interface Props {
  stage: StageType;
  isCurrent?: boolean;
  isActive?: boolean;
  index: number;
  totalStages: number;
  pdfMode?: boolean;
}

export default function Stage({
  stage,
  isCurrent = false,
  isActive = false,
  index,
  totalStages,
  pdfMode = false,
}: Props) {
  const { Icon } = stage;
  const { t } = useTranslation();

  const delay = (index / Math.max(totalStages - 1, 1)) * 0.5;
  const opacity = isActive ? (isCurrent ? 1 : 0.5) : 0;
  const scale = isActive ? (isCurrent ? 1.15 : 0.9) : 0.3;

  return (
    <div
      className="absolute z-20"
      style={{
        left: `${pdfMode ? stage.x - 1 : stage.x}%`,
        top: `${stage.y}%`,
        transform: "translate(0%,-200%)",
      }}
    >
      <motion.div
        initial={pdfMode ? false : { opacity: 0, scale: 0.3 }}
        animate={{ opacity, scale }}
        transition={
          pdfMode ? { duration: 0 } : { duration: 0.3, ease: "easeOut", delay }
        }
        className={`
          relative flex h-9 w-9 items-center justify-center
          rounded-full border-2 bg-white shadow-sm
          sm:h-10 sm:w-10 md:h-11 md:w-11 lg:h-12 lg:w-12
          ${isCurrent ? "ring-4 ring-offset-2" : ""}
        `}
        style={{
          borderColor: stage.color,
          boxShadow: isCurrent
            ? `0 0 40px ${stage.color}60, 0 0 80px ${stage.color}30`
            : undefined,
        }}
      >
        <div
          className="absolute inset-0 rounded-full blur-xl"
          style={{
            background: stage.color,
            opacity: isCurrent ? 0.3 : 0.1,
          }}
        />
        <div
          className="absolute inset-[4px] rounded-full"
          style={{
            background: `${stage.color}${isCurrent ? "20" : "10"}`,
          }}
        />
        <Icon
          size={isCurrent ? 16 : 12}
          color={isCurrent ? stage.color : `${stage.color}60`}
          className="relative z-10 sm:size-3.5 md:size-4 lg:size-6"
        />
      </motion.div>

      <motion.div
        initial={pdfMode ? false : { opacity: 0 }}
        animate={{ opacity }}
        transition={
          pdfMode
            ? { duration: 0 }
            : { duration: 0.2, ease: "easeOut", delay: delay + 0.1 }
        }
        className="absolute left-1/2 top-full -translate-x-1/2 pt-0.5"
        style={{ width: "max-content" }}
      >
        <h4
          className={`
            whitespace-nowrap text-center
            text-[7px] sm:text-[8px] md:text-[10px] lg:text-[12px]
            ${isCurrent ? "font-extrabold" : "font-bold"}
          `}
          style={{ color: stage.color }}
        >
          {stage.title}
        </h4>

        <div
          className={`
            sm:h-5.5 sm:w-5.5 lg:h-6.5 lg:w-6.5 mx-auto mt-0.5 flex
            h-5 w-5 items-center justify-center
            rounded-full text-[8px] font-bold shadow md:h-6
            md:w-6 md:text-[12px]
            ${isCurrent ? "text-white shadow-2xl" : "text-gray-200"}
          `}
          style={{
            background: stage.color,
            boxShadow: isCurrent
              ? `0 4px 20px ${stage.color}80`
              : `0 4px 12px ${stage.color}55`,
          }}
        >
          {stage.id}
        </div>

        {isCurrent && (
          <motion.div
            initial={pdfMode ? false : { opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={
              pdfMode ? { duration: 0 } : { delay: 0.4, duration: 0.2 }
            }
            className="mx-auto mt-1 w-max rounded-full px-2 py-0.5 text-[6px] font-bold text-white sm:text-[7px] md:text-[10px]"
            style={{
              background: stage.color,
              boxShadow: `0 2px 10px ${stage.color}50`,
            }}
          >
            ● {t("assessment.industryLifeCycle.currentPosition")}
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
